import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { DashboardAISummaryControl } from 'apps/sigma2-ui/src/components/dashboards/DashboardAISummaryControl';
import { callCreateDashboardAISummary } from 'apps/sigma2-ui/src/state/dashboard-ai-summary';
import { callQueryDashboardAISummary } from 'apps/sigma2-ui/src/state/dashboard-ai-summary';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { DashboardAISummaryFailureStatus } from 'libs/end-to-end/src/dashboard-ai-summary/dashboard-ai-summary-types';
import { DashboardAISummaryLoadingStatus } from 'libs/end-to-end/src/dashboard-ai-summary/dashboard-ai-summary-types';
import { DashboardAISummaryStatus } from 'libs/end-to-end/src/dashboard-ai-summary/dashboard-ai-summary-types';
import { GenerateReportRequest } from 'libs/end-to-end/src/generate-report';
import { ProsemirrorOutput } from 'libs/end-to-end/src/prosemirror';

const isDashboardAISummaryFailureStatus = (
  status: DashboardAISummaryStatus,
): status is DashboardAISummaryFailureStatus => {
  return DashboardAISummaryFailureStatus.safeParse(status).success;
};

const isDashboardAISummaryLoadingStatus = (
  status: DashboardAISummaryStatus,
): status is DashboardAISummaryLoadingStatus => {
  return DashboardAISummaryLoadingStatus.safeParse(status).success;
};

export const DashboardAISummaryHeaderControl: React.FC<{
  disabled: boolean;
  organizationId: string | null;
  databaseId: string | null;
  databaseRevisionId: string | null;
  dashboardId: string | null;
  generateReportRequest: GenerateReportRequest | null;
  onSummaryReady: (params: {
    summary: ProsemirrorOutput;
    createdAtEpoch: number;
  }) => void;
  onSummaryIntentStart?: () => void;
  onSummaryIntentEnd?: () => void;
}> = ({
  disabled,
  organizationId,
  databaseId,
  databaseRevisionId,
  dashboardId,
  generateReportRequest,
  onSummaryReady,
  onSummaryIntentStart,
  onSummaryIntentEnd,
}) => {
  const { userAuthenticatedApiCall } = useUser();
  const requestVersionRef = useRef(0);
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorStatus, setErrorStatus] =
    useState<DashboardAISummaryFailureStatus>('failed-generating-report');
  const [loadingStatus, setLoadingStatus] =
    useState<DashboardAISummaryLoadingStatus>('pending');
  const [dashboardAISummaryId, setDashboardAISummaryId] = useState<
    string | null
  >(null);

  const resetSummaryRun = useCallback(({ close }: { close: boolean }) => {
    requestVersionRef.current += 1;
    setStatus('idle');
    setErrorStatus('failed-generating-report');
    setLoadingStatus('pending');
    setDashboardAISummaryId(null);
    setIsOpen(!close);
  }, []);

  const isSubmitDisabled = useMemo(() => {
    return (
      disabled ||
      !organizationId ||
      !databaseId ||
      !databaseRevisionId ||
      !dashboardId ||
      !generateReportRequest ||
      generateReportRequest.charts.length === 0
    );
  }, [
    dashboardId,
    databaseId,
    databaseRevisionId,
    disabled,
    generateReportRequest,
    organizationId,
  ]);

  const handleToggle = useCallback(() => {
    if (isSubmitDisabled) {
      return;
    }

    setIsOpen((current) => !current);
  }, [isSubmitDisabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (
      isSubmitDisabled ||
      !organizationId ||
      !databaseId ||
      !databaseRevisionId ||
      !dashboardId ||
      !generateReportRequest
    ) {
      return;
    }

    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    setStatus('loading');
    setErrorStatus('failed-generating-report');
    setLoadingStatus('pending');
    setDashboardAISummaryId(null);
    setIsOpen(true);
    onSummaryIntentStart?.();

    const createResult = await callCreateDashboardAISummary({
      organizationId,
      databaseId,
      databaseRevisionId,
      dashboardId,
      requestBody: {
        generateReportRequest,
        context: context.trim() || undefined,
      },
      userAuthenticatedApiCall,
    });

    // Ignore late create responses after a newer run/reset has taken ownership.
    if (requestVersionRef.current !== requestVersion) {
      return;
    }

    if (!createResult) {
      setErrorStatus('failed-generating-report');
      setStatus('error');
      setDashboardAISummaryId(null);
      setIsOpen(true);
      onSummaryIntentEnd?.();
      return;
    }

    setDashboardAISummaryId(createResult.dashboardAISummaryId);
  }, [
    dashboardId,
    databaseId,
    databaseRevisionId,
    generateReportRequest,
    isSubmitDisabled,
    organizationId,
    context,
    userAuthenticatedApiCall,
    onSummaryIntentEnd,
    onSummaryIntentStart,
  ]);

  const handleCancel = useCallback(() => {
    resetSummaryRun({ close: true });
    onSummaryIntentEnd?.();
  }, [onSummaryIntentEnd, resetSummaryRun]);

  const handleStartOver = useCallback(() => {
    resetSummaryRun({ close: false });
    setContext('');
  }, [resetSummaryRun]);

  const handleSubmitClick = useCallback(() => {
    void handleSubmit();
  }, [handleSubmit]);

  const schedulePoll = useCallback((poll: () => Promise<void>) => {
    return window.setTimeout(() => {
      void poll();
    }, 5000);
  }, []);

  useEffect(() => {
    if (
      status !== 'loading' ||
      !dashboardAISummaryId ||
      !organizationId ||
      !databaseId ||
      !databaseRevisionId ||
      !dashboardId
    ) {
      return;
    }

    let didCancel = false;
    let timeoutId: number | undefined;
    const requestVersion = requestVersionRef.current;

    const poll = async () => {
      const queryResult = await callQueryDashboardAISummary({
        organizationId,
        databaseId,
        databaseRevisionId,
        dashboardId,
        dashboardAISummaryId,
        userAuthenticatedApiCall,
      });

      if (didCancel || requestVersionRef.current !== requestVersion) {
        return;
      }

      if (!queryResult) {
        setErrorStatus('failed-generating-report');
        setStatus('error');
        setDashboardAISummaryId(null);
        setIsOpen(true);
        onSummaryIntentEnd?.();
        return;
      }

      if (queryResult.status === 'success') {
        if (!queryResult.summary) {
          setErrorStatus('failed-generating-report');
          setStatus('error');
          setLoadingStatus('pending');
          setDashboardAISummaryId(null);
          setIsOpen(true);
          return;
        }

        onSummaryReady({
          summary: queryResult.summary,
          createdAtEpoch: queryResult.createdAtEpoch,
        });
        onSummaryIntentEnd?.();
        setStatus('idle');
        setErrorStatus('failed-generating-report');
        setLoadingStatus('pending');
        setDashboardAISummaryId(null);
        setContext('');
        setIsOpen(false);
        return;
      }

      if (isDashboardAISummaryFailureStatus(queryResult.status)) {
        setErrorStatus(queryResult.status);
        setStatus('error');
        setLoadingStatus('pending');
        setDashboardAISummaryId(null);
        setIsOpen(true);
        onSummaryIntentEnd?.();
        return;
      }

      if (isDashboardAISummaryLoadingStatus(queryResult.status)) {
        setLoadingStatus(queryResult.status);
        timeoutId = schedulePoll(poll);
        return;
      }

      setErrorStatus('failed-generating-report');
      setStatus('error');
      setLoadingStatus('pending');
      setDashboardAISummaryId(null);
      setIsOpen(true);
      onSummaryIntentEnd?.();
    };

    void poll();

    return () => {
      didCancel = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    dashboardAISummaryId,
    dashboardId,
    databaseId,
    databaseRevisionId,
    onSummaryReady,
    onSummaryIntentEnd,
    organizationId,
    schedulePoll,
    status,
    userAuthenticatedApiCall,
  ]);

  if (isSubmitDisabled) {
    return (
      <DashboardAISummaryControl
        status="disabled"
        open={false}
        onToggle={() => {}}
      />
    );
  }

  return (
    <ClickAwayListener
      onClickAway={handleClose}
      touchEvent="onTouchStart"
      mouseEvent="onMouseDown"
    >
      <div>
        <DashboardAISummaryControl
          status={status}
          open={isOpen}
          onToggle={handleToggle}
          context={context}
          onContextChange={setContext}
          onSubmit={handleSubmitClick}
          onRetry={handleSubmitClick}
          onStartOver={handleStartOver}
          onCancel={handleCancel}
          errorStatus={errorStatus}
          loadingStatus={loadingStatus}
        />
      </div>
    </ClickAwayListener>
  );
};
